/**
 * language.js - what a recogniser returns is not what a child said, and some of
 * it must never be shown to a parent.
 *
 * PURE. No state, no DOM, no clock.
 *
 * =============================================================================
 * THE PROBLEM, WITH THE ROW THAT PROVED IT
 *
 * A real session, Door Knock, Phase 2 Level 1:
 *
 *   expected "fox"  ->  heard "fuck"   confidence 0.511
 *   expected "crab" ->  heard "crap"   confidence 0.983
 *
 * Neither child said anything of the sort. The Web Speech API does not return
 * what was said; it returns the most probable well-formed English word given
 * the acoustics, and children learning to talk produce exactly the imprecise
 * articulation that makes the nearest dictionary word the wrong one. This
 * platform serves the children most likely to trigger it.
 *
 * Both rows went into `speech_attempt`, permanently, in a field that is shown
 * to a parent.
 *
 * =============================================================================
 * THE DECISION: STORE IT, FLAG IT, NEVER DISPLAY IT
 *
 * Three options were open. The reasoning, because the next person will
 * reasonably want to reopen it:
 *
 * 1. NEVER STORE IT. Rejected. Section 8.6 rule 6 is "log everything", and the
 *    raw result is the only evidence that the RECOGNISER produced the word
 *    rather than the child. Deleting it means the platform cannot answer "why
 *    was my child marked wrong?" - and in the "fox" row above, the stored
 *    alternatives are the only reason anyone found out that the child had in
 *    fact said "fox" and was scored a miss anyway.
 *
 * 2. STORE IT AND NEVER DISPLAY IT, unflagged. Rejected. Without a flag nobody
 *    can find the affected rows, count them, or tell a redaction from a child
 *    who really did say something unexpected.
 *
 * 3. STORE IT, FLAGGED, AND NEVER DISPLAY IT.  <-- taken.
 *
 * Which means, concretely:
 *
 *   - the AUDIT fields keep the text verbatim: `raw_transcript`,
 *     `alternatives`, `trial`, and the per-component reasons. A therapist
 *     asking "what did the microphone actually do" has to be able to find out,
 *     and those fields exist for exactly that.
 *   - the DISPLAY field does not. `speech_attempt.heard` is the field whose
 *     whole job is to be read back by a person, so it is redacted on the way
 *     in and `heard_flagged` records why.
 *   - EVERY parent-facing surface redacts at render as well. Not belt and
 *     braces: a write-time guard cannot do anything about the two rows that are
 *     already stored, and the live interim transcript is drawn on screen
 *     without ever being stored at all.
 *
 * =============================================================================
 * WHY THIS IS IN THE ENGINE
 *
 * It is a property of the recogniser and of English, identical in every game,
 * and no game author will anticipate it. Section 9.1's argument for the chart
 * applies to this too: if each game screened its own output, what a parent can
 * be shown would differ game by game, and one game forgetting is the whole
 * failure. The list below is the platform's, and it is meant to grow.
 */

/**
 * Terms that must never be attributed to a child's speech.
 *
 * Two tiers, because they drive different responses:
 *
 *   severe  a game may not ship a target word whose likely mis-recognition is
 *           one of these (see riskyFor / auditVocabulary). Redaction at display
 *           is not sufficient comfort: the word still enters a child's
 *           permanent record every time the recogniser guesses it.
 *   mild    redacted at display like everything else, but not a reason to
 *           refuse a word - and some of them neighbour vocabulary a game does
 *           not choose (the carrier "want" neighbours a severe term, and the
 *           carrier set is the framework's, section 6.4).
 *
 * Matched on whole tokens with common inflections, never as substrings:
 * substring matching flags "class", "grass" and "Scunthorpe", and a guard that
 * redacts ordinary words teaches everyone to ignore it.
 */
export const SEVERE = Object.freeze([
  'fuck', 'shit', 'cunt', 'bitch', 'dick', 'cock', 'prick', 'twat', 'wank',
  'bollocks', 'bugger', 'slut', 'whore', 'wanker', 'pussy', 'tits', 'titty',
  'nigger', 'nigga', 'paki', 'chink', 'spic', 'kike', 'wog', 'coon',
  'faggot', 'fag', 'dyke', 'tranny', 'retard', 'spastic', 'mong',
  'rape', 'rapist', 'porn', 'penis', 'vagina', 'anus', 'semen',
  'motherfucker', 'bullshit', 'asshole', 'arsehole', 'dickhead',
  'fucker', 'shitter', 'tosser', 'bellend', 'minger',
]);

export const MILD = Object.freeze([
  'crap', 'damn', 'hell', 'ass', 'arse', 'piss', 'bastard', 'bloody',
  'git', 'sod', 'turd', 'fart', 'poo', 'wee', 'bum', 'suck', 'sucks',
  'nut', 'nuts', 'butt', 'balls', 'boob', 'boobs', 'knob', 'willy',
  'sucker', 'crappy', 'pissed', 'farted',
]);

/** Every blocked term, with its tier. */
export const BLOCKED = Object.freeze(
  [...SEVERE.map((term) => ({ term, tier: 'severe' })),
    ...MILD.map((term) => ({ term, tier: 'mild' }))]
);

const TIER_OF = new Map(BLOCKED.map((b) => [b.term, b.tier]));

/**
 * What a person sees instead.
 *
 * Deliberately not "(not recognised)": the result WAS recognised, and saying
 * otherwise would hide a real recogniser failure from the adult who most needs
 * to know about it. This says a word was returned and is not being repeated.
 */
export const FILTERED = '[filtered]';

/**
 * Inflections a blocked term keeps its meaning through.
 *
 * Deliberately short. `-er` was here and it redacted "butter", which is the
 * Scunthorpe problem arriving by the back door: a guard that mangles ordinary
 * words is one everybody learns to ignore. The agent nouns that matter are
 * listed above as terms in their own right instead, which is explicit and has
 * no false positives at all.
 */
const INFLECTIONS = ['', 's', 'es', 'ed', 'd', 'ing', 'in'];

/** A token is blocked if it is a listed term, or that term plus an inflection. */
function tierOfToken(token) {
  const word = token.toLowerCase().replace(/[^a-z]/g, '');
  if (!word) return null;
  if (TIER_OF.has(word)) return TIER_OF.get(word);
  for (const { term, tier } of BLOCKED) {
    if (!word.startsWith(term) || word.length === term.length) continue;
    if (INFLECTIONS.includes(word.slice(term.length))) return tier;
  }
  return null;
}

/**
 * Screen a string that a person might see.
 *
 * @param {string|null|undefined} text
 * @returns {{flagged:boolean, tier:'severe'|'mild'|null, terms:string[],
 *            display:string|null, original:string|null}}
 *          `display` is what may be shown; `original` is returned unchanged so
 *          a caller writing to an AUDIT field can still store it.
 */
export function screenText(text) {
  if (text === null || text === undefined || text === '') {
    return { flagged: false, tier: null, terms: [], display: text ?? null, original: text ?? null };
  }
  const original = String(text);
  const terms = [];
  let worst = null;

  // Split on whitespace, keeping the separators, so redaction preserves the
  // shape of the utterance: "want [filtered]" is more use to an adult than
  // "[filtered]".
  const parts = original.split(/(\s+)/);
  const display = parts.map((part) => {
    const tier = tierOfToken(part);
    if (!tier) return part;
    terms.push(part.toLowerCase().replace(/[^a-z]/g, ''));
    if (tier === 'severe' || worst === null) worst = tier;
    return FILTERED;
  }).join('');

  return {
    flagged: terms.length > 0,
    tier: worst,
    terms,
    display: terms.length ? display : original,
    original,
  };
}

/**
 * The display form, for a caller that wants nothing else.
 * @param {string|null|undefined} text
 * @returns {string|null}
 */
export function redact(text) {
  return screenText(text).display;
}

/**
 * Redact anywhere in a blob of text - a JSON dump, a log line, an error detail.
 *
 * For the surfaces that print structures rather than words. It is cruder than
 * screenText (it cannot see token boundaries inside JSON punctuation) so it
 * looks for the terms wherever they appear between non-letters.
 *
 * @param {string} text
 * @returns {string}
 */
export function redactDeep(text) {
  if (!text) return text;
  return String(text).replace(/[A-Za-z]+/g, (word) => (tierOfToken(word) ? FILTERED : word));
}

// ===========================================================================
// DESIGN TIME - which target words are likely to come back as which obscenity
// ===========================================================================

/**
 * Words a game must not use as targets, with the output that makes them unsafe.
 *
 * EVIDENCE FIRST. The two at the top are not predictions; they are rows in
 * `speech_attempt` from a real session. Everything else is either a case
 * somebody has reported or one the model below found and a person agreed with.
 *
 * This list is the part of the check that carries weight. The phonetic screen
 * after it exists to PROPOSE additions to this list, and is explicitly not
 * trusted on its own - see the note on riskyFor().
 */
export const KNOWN_RISKY = Object.freeze({
  fox: ['fuck'],        // observed: confidence 0.511, Door Knock phase 2 level 1
  crab: ['crap'],       // observed: confidence 0.983, same session
  duck: ['fuck'],
  shirt: ['shit'],
  sit: ['shit'],
  beach: ['bitch'],
  count: ['cunt'],
  sock: ['suck'],
  luck: ['fuck'],
  puck: ['fuck'],
  buck: ['fuck'],
  truck: ['fuck'],
  clock: ['cock'],
  block: ['cock'],
  sheet: ['shit'],
  peanut: ['penis'],
  bunny: ['bunny'],     // placeholder-free: kept out of pools by convention
});

/**
 * A word split into the three parts a recogniser confuses independently.
 *
 * ONSET  the consonants before the vowel
 * VOWEL  one symbol, approximately
 * CODA   the consonants after it
 *
 * The vowel mapping is deliberately crude and is the weakest part of this
 * file: English spelling does not determine a vowel, and "ow" is a different
 * sound in "boat" and "cow". It is good enough to keep "cake" away from "cock"
 * and to keep "fox" next to "fuck", which is the job.
 */
export function syllable(word) {
  let s = String(word || '').toLowerCase().replace(/[^a-z]/g, '');
  if (!s) return null;

  s = s.replace(/^kn/, 'n').replace(/^wr/, 'r').replace(/^gn/, 'n');
  s = s.replace(/ph/g, 'f').replace(/ck/g, 'k').replace(/gh/g, '');
  s = s.replace(/tch/g, 'C').replace(/dge/g, 'j');
  s = s.replace(/sh/g, 'S').replace(/ch/g, 'C').replace(/th/g, 'T');
  s = s.replace(/qu/g, 'kw').replace(/x/g, 'ks').replace(/wh/g, 'w');
  s = s.replace(/c(?=[eiy])/g, 's').replace(/c/g, 'k');

  // The vowel, longest spelling first. `_e` is the magic e that makes "cake"
  // a different word from "cack".
  const VOWELS = [
    [/^(.*?)a([^aeiouy]+)e$/, 'AY'], [/^(.*?)i([^aeiouy]+)e$/, 'IY'],
    [/^(.*?)o([^aeiouy]+)e$/, 'OH'], [/^(.*?)u([^aeiouy]+)e$/, 'OO'],
    [/^(.*?)e([^aeiouy]+)e$/, 'EE'],
    [/^(.*?)(?:ai|ay|ei|ey)(.*)$/, 'AY'], [/^(.*?)(?:ee|ea|ie)(.*)$/, 'EE'],
    [/^(.*?)(?:oa|oe)(.*)$/, 'OH'], [/^(.*?)(?:oo|ew|ue|ui)(.*)$/, 'OO'],
    [/^(.*?)(?:ou|ow)(.*)$/, 'OW'], [/^(.*?)(?:oi|oy)(.*)$/, 'OY'],
    [/^(.*?)(?:ir|er|ur|ear)(?![aeiouy])(.*)$/, 'ER'],
    [/^(.*?)(?:ar)(?![aeiouy])(.*)$/, 'AR'], [/^(.*?)(?:or)(?![aeiouy])(.*)$/, 'OR'],
    [/^(.*?)a(.*)$/, 'A'], [/^(.*?)e(.*)$/, 'E'], [/^(.*?)i(.*)$/, 'I'],
    [/^(.*?)o(.*)$/, 'O'], [/^(.*?)u(.*)$/, 'U'], [/^(.*?)y(.*)$/, 'I'],
  ];
  for (const [pattern, vowel] of VOWELS) {
    const hit = s.match(pattern);
    if (hit) {
      return {
        onset: hit[1].replace(/(.)\1+/g, '$1'),
        vowel,
        coda: (hit[2] || '').replace(/[aeiouy]/g, '').replace(/(.)\1+/g, '$1'),
        // Syllables, roughly. "cat" is not going to come back as "paki".
        groups: (s.match(/[aeiouy]+/g) || []).length,
      };
    }
  }
  return { onset: s, vowel: '', coda: '', groups: 0 };
}

/** Vowels close enough that a child's imprecise one lands on the other. */
const VOWEL_NEIGHBOURS = {
  A: ['E'], E: ['A', 'I'], I: ['E', 'EE', 'ER'], O: ['U'], U: ['O', 'ER', 'OW'],
  EE: ['I'], IY: [], AY: ['E'], OH: ['O'], OO: ['U', 'OW'], OW: ['U', 'OO'],
  OY: [], ER: ['I', 'U'], AR: [], OR: ['O', 'OH'],
};
const vowelClose = (a, b) => a === b || (VOWEL_NEIGHBOURS[a] || []).includes(b);

/**
 * Consonants a recogniser genuinely swaps, listed as pairs rather than by broad
 * class. A class-based rule made /d/ and /k/ interchangeable, which put "dog"
 * one step from an obscenity and made the whole check unusable.
 */
const VOICING = ['pb', 'td', 'kg', 'fv', 'sz', 'Sj', 'Cj', 'TD'];
const SIBILANT = ['sS', 'sT', 'fT', 'zj'];
const SONORANT = ['mn', 'lr', 'rw', 'lw'];
/** Final stops swap place freely - a child fronting or backing the last sound. */
const CODA_STOPS = 'pbtdkg';

const pairIn = (list, a, b) => list.some((pair) => pair.includes(a) && pair.includes(b));

function swappable(a, b, { coda }) {
  if (a === b) return true;
  if (pairIn(VOICING, a, b) || pairIn(SIBILANT, a, b) || pairIn(SONORANT, a, b)) return true;
  return coda && CODA_STOPS.includes(a) && CODA_STOPS.includes(b);
}

/**
 * How a consonant cluster differs from another, or null if too far.
 * @returns {{cost:number, dropped:boolean}|null}
 */
function clusterDiff(target, candidate, coda) {
  if (target === candidate) return { cost: 0, dropped: false };
  // The child under-articulates and a segment disappears: "fox" -> "fuck"
  // loses the /s/, "shirt" -> "shit" loses the /r/. This is the commonest
  // shape of all, which is why it buys a little extra room below.
  if (target.length === candidate.length + 1) {
    for (let i = 0; i < target.length; i += 1) {
      if (target.slice(0, i) + target.slice(i + 1) === candidate) return { cost: 1, dropped: true };
    }
  }
  if (target.length === candidate.length) {
    let differences = 0;
    for (let i = 0; i < target.length; i += 1) {
      if (target[i] === candidate[i]) continue;
      differences += 1;
      if (differences > 1 || !swappable(target[i], candidate[i], { coda })) return null;
    }
    return { cost: 1, dropped: false };
  }
  return null;
}

/**
 * Could a recogniser plausibly return `candidate` when a child aimed at `word`?
 *
 * Structural rather than a distance. A distance cannot tell "fox" -> "fuck"
 * (real, observed) from "boat" -> "shit" (not a thing): both are one step away
 * by any simple metric, and a check that flags a third of an ordinary noun
 * vocabulary is a check everybody learns to ignore.
 *
 * A real confusion keeps the SHAPE of the word:
 *
 *   - the same number of syllables. "cat" does not come back as "paki".
 *   - the onset survives, or swaps for a sound articulated the same way.
 *   - the vowel moves at most to a neighbour.
 *   - the ending loses a sound, or swaps one.
 *
 * and at most ONE of those actually changes - unless the change is a dropped
 * sound, which is what under-articulation does, and then two are allowed. That
 * is the rule that admits "fox" -> "fuck" (vowel moves, /s/ drops) while
 * refusing "cup" -> "cock" (vowel moves, final sound swaps).
 */
export function confusable(word, candidate) {
  const a = syllable(word);
  const b = syllable(candidate);
  if (!a || !b) return false;
  if (a.groups !== b.groups) return false;

  const onset = clusterDiff(a.onset, b.onset, false);
  const coda = clusterDiff(a.coda, b.coda, true);
  if (!onset || !coda) return false;
  if (!vowelClose(a.vowel, b.vowel)) return false;

  const vowelCost = a.vowel === b.vowel ? 0 : 1;
  const cost = onset.cost + coda.cost + vowelCost;
  const dropped = onset.dropped || coda.dropped;
  return dropped ? cost <= 2 : cost <= 1;
}

/**
 * Blocked terms a recogniser could plausibly return in place of this word.
 *
 * NOT a guarantee, in either direction. It is a screen that catches the shape
 * of the confusions seen so far; a recogniser is a language model and will
 * surprise everybody. `KNOWN_RISKY` is the part to trust, this is the part that
 * proposes what to add to it, and `screenText` at display is the only thing
 * that actually protects a parent.
 *
 * @param {string} word
 * @returns {Array<{term:string, tier:string}>} severe first
 */
export function riskyFor(word) {
  const named = KNOWN_RISKY[String(word || '').toLowerCase()] || [];
  const out = [];
  const seen = new Set();
  for (const { term, tier } of BLOCKED) {
    if (!named.includes(term) && !confusable(word, term)) continue;
    if (seen.has(term)) continue;
    seen.add(term);
    out.push({ term, tier, named: named.includes(term) });
  }
  return out.sort((a, b) => (a.tier === b.tier ? 0 : (a.tier === 'severe' ? -1 : 1)));
}

/**
 * Screen a whole vocabulary at design time.
 *
 * @param {string[]} words
 * @returns {{severe:Array, mild:Array}} entries `{ word, risks }`
 */
export function auditVocabulary(words) {
  const severe = [];
  const mild = [];
  for (const word of words) {
    const risks = riskyFor(word);
    if (!risks.length) continue;
    if (risks.some((r) => r.tier === 'severe')) severe.push({ word, risks });
    else mild.push({ word, risks });
  }
  return { severe, mild };
}

export default {
  SEVERE,
  MILD,
  BLOCKED,
  FILTERED,
  KNOWN_RISKY,
  screenText,
  redact,
  redactDeep,
  syllable,
  confusable,
  riskyFor,
  auditVocabulary,
};
